import { translatePropertyFamilyType } from "../translationHelper.js";
import { translate as t } from "../i18nBridge.js";
import { getChildrenTokensForBranch } from "../Utils/hierarchyUtils.js";
import DocumentDO = App.Data.Explorer.DocumentDO;
import ActiveFilterDTO = App.Data.Explorer.Filter.ActiveFilterDTO;
import type {
	DescriptionLine,
	DescriptionToken,
} from "../descriptionTokens.js";
import { parse } from "../Utils/datemath.js";

export type {
	DescriptionLine,
	DescriptionToken,
} from "../descriptionTokens.js";

type FilterType = "time_range" | "hierarchy" | "default";

const DATE_FORMAT = "DD MMM YYYY HH:mm";

let lastMaxWidth = -1;
let lastDocumentUuid = "";
let lastTokenLines: DescriptionLine[] = [];
let lastHierarchyNodeCount = -1;

const tokensText = (tokens: DescriptionLine): string =>
	tokens.map((token) => token.text).join("");

const splitTokenLines = (
	tokens: DescriptionLine,
	maxWidth: number,
	ctx: CanvasRenderingContext2D
): DescriptionLine[] => {
	const lines: DescriptionLine[] = [];
	let currentLine: DescriptionLine = [];
	let currentText = "";

	for (const token of tokens) {
		const testText = currentText + token.text;
		if (currentText.length <= 1 || ctx.measureText(testText).width < maxWidth) {
			currentLine.push(token);
			currentText = testText;
		} else {
			lines.push(currentLine);
			currentLine = [token];
			currentText = token.text;
		}
	}

	if (currentLine.length) lines.push(currentLine);
	return lines;
};

const trimLineStart = (line: DescriptionLine): DescriptionLine => {
	if (!line.length) return line;
	const [first, ...rest] = line;
	return [{ ...first, text: first.text.trimStart() }, ...rest];
};

function getFilterType(filter: ActiveFilterDTO): FilterType {
	if (filter.values[0]?.label === "n/a") return "time_range";
	if (filter.dimension.hierarchy_definition_id) return "hierarchy";
	return "default";
}

function getPrefix(filter: ActiveFilterDTO, filterType: FilterType) {
	const translationKey =
		filterType === "time_range"
			? `property_families.${filter.dimension.family_name_clean}`
			: `properties.${filter.dimension.name_clean}`;
	const translatedFilterName = t(`${translationKey}.title`);
	const operator = filter.valuesAreIncluded ? "=" : "≠";

	return `${translatedFilterName} ${operator} {`;
}

function getData(
	derivedDocument: DocumentDO,
	hierarchyNodes: Record<number, ActiveFilterDTO>,
	filter: ActiveFilterDTO,
	filterType: FilterType
): DescriptionLine[] {
	if (filterType === "time_range") {
		const filterPeriod = filter.values[0].filter_period;
		const from = parse(filterPeriod?.from)?.format(DATE_FORMAT);
		const to = parse(filterPeriod?.to, true)?.format(DATE_FORMAT);

		if (from && to) {
			return [[{ text: `${from} - ${to}` }]];
		}
		if (from) {
			return [[{ text: `≥ ${from}` }]];
		}
		if (to) {
			return [[{ text: `≤ ${to}` }]];
		}

		return [[{ text: "any time" }]];
	}
	if (filterType === "hierarchy") {
		const hierarchyKey = JSON.stringify({
			id: filter.dimension.hierarchy_definition_id,
			name_style: derivedDocument.hierarchy_naming_style,
		});
		const node = hierarchyNodes[hierarchyKey];

		return node ? getChildrenTokensForBranch(node, filter.values[0]) : [];
	}
	if (filterType === "default") {
		return filter.values
			.toSorted((a, b) => (a?.sort_order ?? -1) - (b?.sort_order ?? -1))
			.map((v) => [{ text: v.label }]);
	}
	return [];
}

function sliceData(
	prefix: string,
	data: DescriptionLine[],
	ctx,
	maxWidth: number
): DescriptionLine[] {
	let accWidth = ctx.measureText(`& ${prefix}}`).width;

	for (const [i, line] of data.entries()) {
		accWidth += ctx.measureText(tokensText(line)).width;
		if (accWidth > maxWidth) {
			const sliced = data.slice(0, i + 1);
			const remaining = data.length - 1 - i;

			if (remaining > 0) {
				sliced.push([{ text: `+${remaining} more` }]);
			}
			return sliced;
		}
	}
	return data;
}

function getFilterTokens(
	derivedDocument: DocumentDO,
	hierarchyNodes: Record<number, ActiveFilterDTO>,
	ctx,
	maxWidth: number
): DescriptionLine {
	const activeFilters = derivedDocument.active_filters.filter(
		(filter) => filter.isApplied && !filter.isDefault && filter.values.length
	);

	if (!activeFilters.length) return [];

	const tokens: DescriptionLine = [{ text: "Active Filters | " }];

	for (let i = 0; i < activeFilters.length; i++) {
		if (i > 0) tokens.push({ text: " & " });

		const filter = activeFilters[i];
		const filterType = getFilterType(filter);
		const prefix = getPrefix(filter, filterType);
		const data = getData(derivedDocument, hierarchyNodes, filter, filterType);
		const sliced = sliceData(prefix, data, ctx, maxWidth);

		tokens.push({ text: prefix });
		for (let j = 0; j < sliced.length; j++) {
			if (j > 0) tokens.push({ text: ", " });
			tokens.push(...sliced[j]);
		}
		tokens.push({ text: "}" });
	}

	return tokens;
}

function getConfigTokens(derivedDocument: DocumentDO): DescriptionLine {
	const tokens: DescriptionLine = [
		{
			text: `Chart Configuration | X: ${translatePropertyFamilyType(
				derivedDocument.horizontal_dimension
			)}`,
		},
	];

	if (derivedDocument.offset_dimension?.name_clean) {
		tokens.push({ text: ", " });
		tokens.push({
			text: `Offset: ${translatePropertyFamilyType(
				derivedDocument.offset_dimension
			)}`,
		});
	}
	if (derivedDocument.legend_dimension?.name_clean) {
		tokens.push({ text: ", " });
		tokens.push({
			text: `Legend: ${translatePropertyFamilyType(
				derivedDocument.legend_dimension
			)}`,
		});
	}
	return tokens;
}

function getContextData(context) {
	const { chart, scale } = context;
	const ctx = chart?.ctx;
	const snapshotWidth =
		chart?.$snapshotDate?.width ||
		ctx?.measureText(chart?.$snapshotDate?.snapshotText)?.width ||
		0;
	const totalWidth = scale.width;
	const maxWidth = Math.max(totalWidth - snapshotWidth - 5, 100); // 5px padding

	return {
		ctx,
		maxWidth,
	};
}

function isCacheValid(
	maxWidth: number,
	documentUuid: string,
	hierarchyNodes: Record<number, ActiveFilterDTO>
): boolean {
	const hierarchyNodeCount = Object.keys(hierarchyNodes).length;

	const isValid =
		lastMaxWidth === maxWidth &&
		lastDocumentUuid === documentUuid &&
		lastTokenLines.length > 0 &&
		lastHierarchyNodeCount !== hierarchyNodeCount;

	lastMaxWidth = maxWidth;
	lastDocumentUuid = documentUuid;
	lastHierarchyNodeCount = hierarchyNodeCount;

	return isValid;
}

export function getChartConfig(
	derivedDocument: DocumentDO,
	showConfigurationText: boolean,
	hierarchyNodes: Record<number, ActiveFilterDTO>,
	context
): DescriptionLine[] {
	if (!showConfigurationText) return [];

	const { ctx, maxWidth } = getContextData(context);

	// Return cached result when dimensions/size haven't changed
	if (isCacheValid(maxWidth, derivedDocument.uuid, hierarchyNodes)) {
		return lastTokenLines;
	}

	const configTokens = getConfigTokens(derivedDocument);
	const filterTokens = getFilterTokens(
		derivedDocument,
		hierarchyNodes,
		ctx,
		maxWidth / 2
	);
	const configLines = splitTokenLines(configTokens, maxWidth, ctx);
	const filterLines = splitTokenLines(filterTokens, maxWidth, ctx);

	lastTokenLines = [...configLines, ...filterLines].map(trimLineStart);
	return lastTokenLines;
}
